import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, type RequestBody } from '../client';
import { queryKeys } from '../react-query.config';
import { toast } from '@/hooks/use-toast';

export const useSubmitSelections = ({
  pickemId,
  isUpdate,
  onSuccess,
  showToast = true,
}: {
  pickemId: string;
  isUpdate?: boolean;
  onSuccess?: () => void;
  showToast?: boolean;
}) => {
  const queryClient = useQueryClient();

  type SelectionPayload = RequestBody<'/api/pickems/{id}/selections', 'post'>;

  const mutation = useMutation({
    mutationKey: ['submitSelections', pickemId],
    mutationFn: async (data: SelectionPayload) => {
      return apiClient.post('/api/pickems/{id}/selections', data, {
        pathParams: { id: pickemId },
      });
    },
    onSuccess: async _data => {
      // Refetch the specific pickem detail
      await queryClient.refetchQueries({
        queryKey: queryKeys.pickem.detail(pickemId),
      });

      // Refetch (not just invalidate) all pickems to ensure has_entered is updated
      // This prevents race condition where UI shows stale data before DB update completes
      await queryClient.refetchQueries({
        queryKey: queryKeys.pickem.all,
      });

      if (showToast) {
        toast({
          title: 'Success',
          description: isUpdate
            ? 'Your selections have been updated successfully.'
            : 'Your selections have been submitted successfully.',
        });
      }

      onSuccess?.();
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit selections. Please try again.',
        variant: 'destructive',
      });
    },
  });

  return mutation;
};
